import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import { Tab, Tabs } from '@material-ui/core';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Spinner } from 'core/components/spinner';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { useStore } from 'core/store';
import { InventoryCategory } from '../inventory-category';
import parseInventoryItems from '../../helpers/parse-inventory-items';

import useStyles from './review-inventory-images-modal.styles';

const TABS = {
  AUTO: 'AUTO',
  HOME: 'HOME',
  UMBRELLA: 'UMBRELLA',
  SPP: 'SPP'
};

/** Group SPP items by category and render each as a separate InventoryCategory section */
const renderSPPCategories = (sppItems) => {
  const sppByCategory = {};
  const categoryItemCounts = {};
  sppItems.forEach((item) => {
    const cat = item.category || 'SPP';
    if (!sppByCategory[cat]) {
      sppByCategory[cat] = [];
      categoryItemCounts[cat] = 0;
    }
    sppByCategory[cat].push(item);
  });
  Object.keys(sppByCategory).forEach((cat) => {
    const uniqueItems = new Set(sppByCategory[cat].map((i) => i.itemId));
    categoryItemCounts[cat] = uniqueItems.size;
  });
  const sortedCategories = Object.keys(sppByCategory).sort((a, b) => a.localeCompare(b));
  return sortedCategories.map((cat) => (
    <Grid xs={12} key={`spp-${cat}`}>
      <InventoryCategory
        category="spp"
        categoryTitle={cat}
        items={sppByCategory[cat]}
        itemCount={categoryItemCounts[cat]}
      />
    </Grid>
  ));
};

const ReviewInventoryImagesModal = observer(({ open, onClose }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(TABS.AUTO);
  const { account } = useStore();
  const { inventoryItems = [], inventoryItemsLoading, fetchInventoryItems } = account;

  useEffect(() => {
    if (open && !inventoryItemsLoading && !inventoryItems.length) {
      fetchInventoryItems();
    }
  }, [fetchInventoryItems, open, inventoryItemsLoading, inventoryItems]);

  const {
    parsedItems,
    categoryItemCount,
    hasAutoImages,
    hasHomeImages,
    hasUmbrellaImages,
    hasSppImages,
    homeCategories,
    umbrellaCategories
  } = parseInventoryItems(inventoryItems);

  useEffect(() => {
    if (hasAutoImages) {
      setTab(TABS.AUTO);
    } else if (hasHomeImages) {
      setTab(TABS.HOME);
    } else if (hasUmbrellaImages) {
      setTab(TABS.UMBRELLA);
    } else if (hasSppImages) {
      setTab(TABS.SPP);
    }
  }, [hasAutoImages, hasHomeImages, hasUmbrellaImages, hasSppImages]);

  const hasInventoryImages = hasAutoImages || hasHomeImages || hasUmbrellaImages;
  let modalHeader;
  if (hasInventoryImages && hasSppImages) {
    modalHeader = 'Self-guided inspection and SPP images';
  } else if (hasSppImages) {
    modalHeader = 'SPP images';
  } else {
    modalHeader = 'Self-guided inspection images';
  }

  return (
    <BaseDialog open={open} onClose={onClose} classes={{ paper: classes.container }}>
      <Label type="greenBig" className={classes.modalHeader}>
        {modalHeader}
      </Label>
      <Tabs value={tab} variant="fullWidth" onChange={(_, newTab) => setTab(newTab)} className={classes.bottomSpacing}>
        {hasAutoImages && <Tab value={TABS.AUTO} label="Auto Images" className={classes.tab} />}
        {hasHomeImages && <Tab value={TABS.HOME} label="Home Images" className={classes.tab} />}
        {hasUmbrellaImages && <Tab value={TABS.UMBRELLA} label="Umbrella Images" className={classes.tab} />}
        {hasSppImages && <Tab value={TABS.SPP} label="SPP Images" className={classes.tab} />}
      </Tabs>

      {inventoryItemsLoading ? (
        <div className={classes.loadingContainer}>
          <Spinner size={102} />
        </div>
      ) : (
        <>
          {/* AUTO IMAGES */}
          {tab === TABS.AUTO && (
            <Grid xs={12} key="cars">
              <InventoryCategory category="cars" items={parsedItems.auto} itemCount={categoryItemCount.auto} />
            </Grid>
          )}

          {/* HOME IMAGES */}
          {tab === TABS.HOME &&
            homeCategories.map((category) => (
              <Grid xs={12} key={category}>
                <InventoryCategory
                  category={category}
                  items={parsedItems[category]}
                  itemCount={categoryItemCount[category]}
                />
              </Grid>
            ))}

          {/* UMBRELLA IMAGES */}
          {tab === TABS.UMBRELLA &&
            umbrellaCategories.map((category) => (
              <Grid xs={12} key={category}>
                <InventoryCategory
                  category={category}
                  items={parsedItems[category]}
                  itemCount={categoryItemCount[category]}
                />
              </Grid>
            ))}

          {/* SPP IMAGES — grouped by category (Jewelry, Cameras, etc.) */}
          {tab === TABS.SPP && parsedItems.spp && renderSPPCategories(parsedItems.spp)}
        </>
      )}

      <Grid className={classes.btnContainer}>
        <Button variant="contained" color="primary" className={classes.closeBtn} onClick={onClose}>
          Close
        </Button>
      </Grid>
    </BaseDialog>
  );
});

ReviewInventoryImagesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  inventoryItems: PropTypes.object.isRequired
};

export default ReviewInventoryImagesModal;
